import { useCallback, useEffect, useId, type KeyboardEvent, type RefObject } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { preventDefault } from 'lib/web/eventHandlerUtils'
import type { ReferenceType } from '@floating-ui/react-dom'
import type { TextDirection } from 'lib/dom/logicalScroll'
import type { TripDetail } from 'tripPlanner/api/tripDetails'
import { usePromptPosition, type PromptPositionOptions } from 'tripPlanner/components/Chat/hooks/usePromptPosition'
import PromptCard from 'tripPlanner/components/Chat/PromptCard'
import PromptTail, { PROMPT_TAIL_SIZE } from 'tripPlanner/components/Chat/PromptTail'
import TripDetailsNudgeAnswer from './TripDetailsNudgeAnswer'
import { ANSWERS, type NudgeOption } from './tripDetailsNudgeAnswers'
import tripDetailsNudgeMessages, { TRIP_DETAIL_QUESTION_MESSAGES } from './tripDetailsNudgeMessages'

const TAIL_GAP = 4
// How far into the card the tail sits, so the caret it points at lines up with the start of the question.
const CARET_INSET = 24
const CLOSE_ICON = <LineTimesIcon />
// Flipping below would put the card over the composer's controls.
const NUDGE_POSITION: PromptPositionOptions = {
  placement: 'top-start',
  offset: { mainAxis: PROMPT_TAIL_SIZE + TAIL_GAP, crossAxis: -CARET_INSET },
  arrowPadding: 12,
  canFlip: false,
}

// Sheet's padding prop is a physical shorthand, so the logical padding lives here.
const Card = styled(PromptCard)`
  inline-size: max-content;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  column-gap: ${rem(4)};
  padding-block: ${rem(8)};
  padding-inline: ${rem(12)} ${rem(6)};
`

// The question and close button each take an answer's height, so all three share a centre line.
const Line = styled.div`
  display: flex;
  align-items: center;
  min-block-size: ${rem(32)};
`

interface Props {
  anchor: ReferenceType | null
  /** The nudge slides sideways within this, so it stays over the composer */
  boundary?: Element
  textDirection: TextDirection
  detail: TripDetail
  showsAnswers: boolean
  /** Where keyboard focus goes when the button it was on goes away with the question */
  returnFocusRef: RefObject<HTMLElement | null>
  /** The question is on screen, not just rendered */
  onShown: (detail: TripDetail) => void
  onAnswer: (detail: TripDetail, option: NudgeOption, sentence: string) => void
  onDismiss: (detail: TripDetail) => void
}

function TripDetailsNudge({ anchor, boundary, textDirection, detail, showsAnswers, returnFocusRef, onShown, onAnswer, onDismiss }: Props) {
  const intl = useIntl()
  const questionId = useId()
  const { setFloating, floatingElement, floatingStyle, arrowRef, arrowStyle, side, isVisible } = usePromptPosition(anchor, textDirection, NUDGE_POSITION, boundary)

  useEffect(() => {
    if (isVisible) {
      onShown(detail)
    }
  }, [detail, isVisible, onShown])

  // Focus inside the card came from the keyboard; moving it to the draft after a tap would open a mobile keyboard.
  const returnFocus = useCallback(() => {
    if (floatingElement?.contains(document.activeElement)) {
      returnFocusRef.current?.focus()
    }
  }, [floatingElement, returnFocusRef])

  const handleAnswer = useCallback((option: NudgeOption, sentence: string) => {
    returnFocus()
    onAnswer(detail, option, sentence)
  }, [detail, onAnswer, returnFocus])

  const handleDismiss = useCallback(() => {
    returnFocus()
    onDismiss(detail)
  }, [detail, onDismiss, returnFocus])

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') handleDismiss()
  }, [handleDismiss])

  return (
    <Card
      ref={setFloating}
      style={floatingStyle}
      className={clsx({ visible: isVisible })}
      background="normal-background"
      border="light-foreground"
      borderRadius="M"
      shadow="bottom-medium"
      role="group"
      aria-labelledby={questionId}
      onKeyDown={handleKeyDown}
    >
      <PromptTail ref={arrowRef} side={side} style={arrowStyle} />
      <Group direction="horizontal" wrap="wrap" gap="8 12" verticalAlign="center">
        <Line>
          <BodyText id={questionId} variant="medium">
            {intl.formatMessage(TRIP_DETAIL_QUESTION_MESSAGES[detail])}
          </BodyText>
        </Line>
        {/* Answers wrap as a pair, so one never sits beside the question with the other below. */}
        {showsAnswers && (
          <Group direction="horizontal" gap={8}>
            {ANSWERS[detail].map(answer => (
              <TripDetailsNudgeAnswer key={answer.key} answer={answer} onClick={handleAnswer} />
            ))}
          </Group>
        )}
      </Group>
      <Line>
        <IconButton
          kind="tertiary"
          shape="circle"
          size="small"
          aria-label={intl.formatMessage(tripDetailsNudgeMessages.dismiss)}
          onMouseDown={preventDefault}
          onClick={handleDismiss}
        >
          {CLOSE_ICON}
        </IconButton>
      </Line>
    </Card>
  )
}

export default TripDetailsNudge
